import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaShieldAlt, FaLeaf, FaCheckCircle } from "react-icons/fa";
import "./Home.css";
import BookingForm from "../components/BookingForm.js";
import BeforeAfterSlider from "../components/BeforeAfterSlider.js";
import ServiceQuoteForm from "../components/ServiceQuoteForm.js";
import useScrollReveal from "../hooks/useScrollReveal.js";
import useSeo, { buildBreadcrumbs } from "../hooks/useSeo.js";

const odourTreatmentList = [
  {
    issue: "Pet urine and accidents",
    strategy:
      "Targeted neutralisation of uric acid crystals and ammonia compounds to permanently eliminate organic smells.",
  },
  {
    issue: "Food and drink spills",
    strategy:
      "Specialised chemical spot treatments formulated to match the specific acid or oil profile of the spill.",
  },
  {
    issue: "Smoke and stale odours",
    strategy:
      "Comprehensive fabric deodorisation to capture and flush out microscopic airborne particulate residues.",
  },
  { issue: "Everyday household smells" },
  {
    issue: "Embedded pet hair and dirt",
    strategy:
      "High-friction mechanical harvesting and industrial pre-vacuuming before water extraction.",
  },
];

const whyChooseUs = [
  {
    title: "Rapid Dry Times",
    tagline: "Clean today. Back to living sooner.",
    description:
      "Our controlled extraction process removes excess moisture, helping carpets and upholstery dry faster without leaving them unnecessarily soaked.",
  },
  {
    title: "No Sticky Residue",
    tagline: "Nothing left behind.",
    description:
      "We use the correct products and extraction methods to minimise residue that can attract more dirt after cleaning.",
  },
  {
    title: "Eco-Friendly & Family-Safe",
    tagline: "Tough on dirt. Thoughtful around your home.",
    description:
      "We choose cleaning solutions with your family, children and pets in mind while still delivering a powerful professional clean.",
  },
  {
    title: "Personalised Fibre Care",
    tagline: "Different fibres deserve different treatment.",
    description:
      "Natural wool, synthetics, silks and fine linens each demand distinct chemical, temperature and pressure combinations — we inspect the material before cleaning so your carpet, rug, couch or mattress receives a treatment suited to its fibre, condition and cleaning needs.",
  },
  {
    title: "Honest Results",
    tagline: "No pressure. No unrealistic promises. Just professional care.",
    description: "We assess every item honestly and explain the best treatment before beginning.",
  },
];

const Home = () => {
  const [showForm, setShowForm] = useState(false);
  const [openWhyChoose, setOpenWhyChoose] = useState(null);
  // Tracks which "Why Choose" cards should be forced visible regardless of the scroll-reveal
  // observer's own state. Needed because toggling isOpen changes that card's className (adding
  // "accordion-item-open"), and React's re-render then overwrites the DOM's className attribute
  // wholesale — silently wiping the "reveal-visible" class the observer had added outside React's
  // knowledge. Keeping it in React state instead means it's part of the className React computes,
  // so it survives every re-render instead of being wiped by the very click that opens the card.
  const [revealedWhyChoose, setRevealedWhyChoose] = useState(() => new Set());
  const location = useLocation();
  const navigate = useNavigate();
  const bookingSectionRef = useRef(null);
  const quoteSectionRef = useRef(null);
  const homeRef = useRef(null);

  useScrollReveal(homeRef);
  useSeo({
    title: "Carpet Cleaning Perth | Rug, Upholstery, Mattress & Window Cleaning | Aeti",
    rawTitle: true,
    description:
      "Professional carpet cleaning in Perth plus rug, couch, upholstery, mattress, curtain, blind and window cleaning across Perth and surrounding suburbs.",
    path: "/",
    structuredData: buildBreadcrumbs([{ name: "Home", path: "/" }]),
  });

  useEffect(() => {
    if (location.state?.scrollToBooking) {
      setShowForm(true);
      const timer = window.setTimeout(() => {
        bookingSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);

      return () => window.clearTimeout(timer);
    }
  }, [location.state]);

  const handleBookService = () => {
    setShowForm(true);
    window.setTimeout(() => {
      bookingSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  const handleRequestQuote = () => {
    quoteSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const goToCleaningProcess = () => navigate("/cleaning-process");

  return (
    <div className="home" ref={homeRef}>
      <section className="hero-section">
        <div className="hero-copy">
          <p className="eyebrow hero-eyebrow reveal">Premium Carpet, Rug, Upholstery &amp; Couches Cleaning in Perth</p>
          <h1 className="reveal" style={{ transitionDelay: "60ms" }}>
            Bring Back the Glow to the Home You Know.
          </h1>
          <p className="reveal" style={{ transitionDelay: "120ms", fontWeight: "bold" }}>
            Stains, spills, pet accidents or tired-looking new carpet and rugs?
          </p>
          <p className="hero-text reveal" style={{ transitionDelay: "160ms" }}>
            Aeti provides professional Carpet Cleaning and Specialist Cleaning Services for Rug, Couch, Upholstery, Mattress, Blinds, Curtains and Windows across Perth and Western Australia.
          </p>
          <p className="hero-text hero-tagline reveal" style={{ transitionDelay: "200ms" }}>
            Don’t replace it—revive it with Aeti.
          </p>
          <div className="hero-motto reveal" style={{ transitionDelay: "220ms" }}>
            <span className="hero-motto-item">
              <FaShieldAlt aria-hidden="true" />
              Trusted
            </span>
            <span className="hero-motto-item">
              <FaLeaf aria-hidden="true" />
              Eco-Friendly
            </span>
            <span className="hero-motto-item">
              <FaCheckCircle aria-hidden="true" />
              Honest Results
            </span>
          </div>
          <div className="hero-actions reveal" style={{ transitionDelay: "240ms" }}>
            <button className="primary-btn hero-cta" onClick={handleBookService}>
              Book (Aeti IT) Today
            </button>
            <button className="secondary-btn hero-cta" onClick={handleRequestQuote}>
              Request for Fast Quote
            </button>
          </div>
          <div className="hero-stats reveal" style={{ transitionDelay: "280ms" }}>
            <div>
              <strong>24/7</strong>
              <span>Booking support</span>
            </div>
            <div>
              <strong>100%</strong>
              <span>Client-focused care</span>
            </div>
          </div>
        </div>

        <div className="hero-card reveal" style={{ transitionDelay: "160ms" }}>
          <img src={require("../assets/carpet&rug.jpg")} alt="Professional cleaning team" style={{ transitionDelay: "160ms", width: "100%", height: "auto" }} />
        </div>
      </section>

      <section className="section quote-calculator-section" id="quote-calculator" ref={quoteSectionRef}>
        <div className="section-heading reveal">
          <p className="eyebrow">Instant estimate</p>
          <h2>Generate your quote within seconds</h2>
          <p className="reveal" style={{ transitionDelay: "120ms", fontWeight: "bold" }}>
            Blink and it's done
          </p>

        </div>
        <div className="quote-calculator-layout reveal">
          <div className="quote-calculator-image">
            <img src={require("../assets/carpet&rug.jpg")} alt="Aeti cleaning technician at work in a Perth home" />
          </div>
          <div className="quote-calculator-panel">
            <div className="quote-tab-panel">
              <ServiceQuoteForm seoEnabled={false} />
            </div>
          </div>
        </div>
      </section>

      <section
        className={`section booking-section ${showForm ? "" : "booking-section-empty"}`}
        id="book-now"
        ref={bookingSectionRef}
      >
        {showForm && (
          <BookingForm
            prefillContact={location.state?.prefillContact}
            prefillCategories={location.state?.prefillCategories}
            prefillDetails={location.state?.prefillDetails}
          />
        )}
      </section>

      <section className="section transformation-section">
        <div className="section-heading reveal">
          <p className="eyebrow">See the Difference</p>
          <h2>Drag to see the transformation.</h2>
        </div>
        <div className="reveal">
          <BeforeAfterSlider
            beforeSrc={require("../assets/carpet&rug-before.png")}
            afterSrc={require("../assets/carpet&rug.jpg")}
            beforeAlt="Carpet with visible dirt before Aeti deep cleaning"
            afterAlt="Same carpet, clean and refreshed after Aeti deep cleaning"
          />
        </div>
      </section>

      <section className="section odour-section">
        <div className="section-heading reveal">
          <p className="eyebrow">Pet Urine, Spills &amp; Odour Treatment</p>
          <h2>We Do Not Mask Odours. We Target the Source.</h2>
        </div>
        <div className="odour-panel">
          <div className="odour-copy reveal">
            <p>
              Pet urine, food spills, smoke and everyday odours can settle deep inside carpets,
              rugs and upholstery.
            </p>
            <p>
              Our targeted treatment is designed to break down the source of the odour—not
              simply cover it with fragrance.
            </p>
            <p className="highlight-tagline">
              Because your home should smell clean—not chemically covered.
            </p>
            <p className="odour-disclaimer">
              Treatment results depend on the age, depth and condition of the affected area,
              but we will always explain what can realistically be achieved.
            </p>
          </div>
          <ul className="odour-list reveal" style={{ transitionDelay: "120ms" }}>
            {odourTreatmentList.map((item, index) => (
              <li key={item.issue} style={{ transitionDelay: `${120 + index * 60}ms` }}>
                <span className="odour-issue">{item.issue}</span>
                {item.strategy && <span className="odour-strategy">{item.strategy}</span>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="section-heading reveal">
          <h2>Why Choose Aeti Cleaning Services?</h2>
        </div>
        <div className="accordion-list">
          {whyChooseUs.map((item, index) => {
            const isOpen = openWhyChoose === index;
            return (
              <article
                className={`accordion-item reveal ${isOpen ? "accordion-item-open" : ""} ${
                  revealedWhyChoose.has(index) ? "reveal-visible" : ""
                }`}
                key={item.title}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <button
                  type="button"
                  className="accordion-trigger"
                  aria-expanded={isOpen}
                  onClick={() => {
                    setOpenWhyChoose(isOpen ? null : index);
                    // Interacting with a card is proof it's meant to be visible, so force it into
                    // the "revealed" state via React (see revealedWhyChoose above) rather than
                    // relying on the scroll-reveal IntersectionObserver alone.
                    setRevealedWhyChoose((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
                  }}
                >
                  <span className="accordion-trigger-text">
                    <span className="accordion-title">{item.title}</span>
                    <span className="highlight-tagline">{item.tagline}</span>
                  </span>
                  <span className="accordion-icon" aria-hidden="true">▾</span>
                </button>
                {isOpen && (
                  <div className="accordion-panel">
                    <p>{item.description}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="section">
        <div className="section-heading reveal">
          <p className="eyebrow">How Aeti Is Different</p>
          <h2>We Clean What Others Miss</h2>
        </div>
        <div className="different-layout">
          <div className="different-copy">
            <div className="different-panel reveal">
              <p>Some cleaners arrive, spray, steam and leave.</p>
              <p>At Aeti, every service begins with understanding what we are cleaning.</p>
              <p>
                We inspect the fibres, identify stains, remove dry soil and select the right
                treatment before moisture touches the surface.
              </p>
              <p className="highlight-tagline">Less guesswork. More care. Better-looking results.</p>
              <p>
                We treat every carpet, rug and piece of furniture as if it matters—because to you,
                it may be more than an item.
              </p>
              <p>It may be the couch where your children grew up.</p>
              <p>The rug you brought from overseas.</p>
              <p>The furniture chosen for your first home.</p>
              <p>A piece connected to someone or somewhere special.</p>
            </div>

            <div className="different-panel reveal">
              <h3>Extend the Life of Your Interior</h3>
              <p>
                Replacing your carpets, rugs and furniture can be expensive—and some things cannot
                truly be replaced.
              </p>
              <p>
                Our goal is to protect what you already own, revive what everyday life has worn
                down and help your interior remain part of your home for longer.
              </p>
              <p className="highlight-tagline">Restore the beauty. Preserve the memories. Extend the life.</p>
            </div>
          </div>

          <div className="different-image reveal">
            <img
              src={require("../assets/hero.jpg")}
              alt="Aeti technician carefully cleaning every edge and surface, missing nothing"
            />
          </div>
        </div>
      </section>

      <section className="section process-teaser-section">
        <div className="section-heading reveal">
          <p className="eyebrow">Aeti Process</p>
          <h2>Our Five Steps Professional Carpet Cleaning Process</h2>
        </div>
        <div className="process-teaser-panel reveal">
          <p>
            Every Aeti Carpet Cleaning service follows a careful five-step process: fibre
            inspection, industrial pre-vacuuming, targeted stain treatment, deep hot water
            extraction and professional carpet grooming.
          </p>
          <button className="secondary-btn" onClick={goToCleaningProcess}>
            See our full process
          </button>
        </div>
      </section>

      <section className="section closing-section">
        <div className="section-heading reveal">
          <h2>Do Not Replace It Yet</h2>
          <p>
            That stained carpet, tired couch or dull rug may still have life left — let Aeti
            assess it before you spend thousands replacing something professional cleaning could
            revive.
          </p>
        </div>
        <div className="closing-brand reveal">
          <p className="closing-brand-name">Aeti Cleaning Services</p>
          <p className="closing-brand-tagline">
            Bringing life back to the surfaces that hold your life.
          </p>
          <div className="hero-actions">
            <button className="primary-btn" onClick={handleBookService}>
              Book Now
            </button>
            <button className="secondary-btn" onClick={handleRequestQuote}>
              Request a Quote
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
